import { useCallback, useMemo } from "react";
import { EDamageSeverity } from "@inbound/api";
import { IconButton, LayoutJay, Typography } from "@lux/ds";
import { Add, Remove } from "@lux/ds/Icon/MaterialIcons";
import { StyledButton } from "./DamageSeverityTicker.styles";
import useContent from "@@hooks/useContent";

enum EChangeSeverityType {
  Decrease = 0,
  Increase = 1,
}

interface IDamageSeverityTickerProps {
  isDisabled: boolean;
  label: string;
  onChangeSeverity: (severity: EDamageSeverity | null) => void;
  value: EDamageSeverity | null;
}

const severityLevels = [
  null,
  EDamageSeverity.Low,
  EDamageSeverity.Medium,
  EDamageSeverity.High,
];

const DamageSeverityTicker: React.FC<IDamageSeverityTickerProps> = ({
  isDisabled,
  label,
  onChangeSeverity,
  value,
}) => {
  const {
    content: { itemReview: content },
  } = useContent();

  const stringifiedValue = value ?? "null";

  const currentValueIndex = useMemo(
    () => severityLevels.findIndex((severityLevel) => value === severityLevel),
    [value],
  );

  const isIncreaseDisabled = useMemo(
    () => currentValueIndex === severityLevels.length - 1,
    [currentValueIndex],
  );

  const handleChangeSeverity = useCallback(
    (type: EChangeSeverityType) => {
      if (type === EChangeSeverityType.Increase) {
        onChangeSeverity(severityLevels[currentValueIndex + 1]);
      } else {
        onChangeSeverity(severityLevels[currentValueIndex - 1]);
      }
    },
    [currentValueIndex, onChangeSeverity],
  );

  const severityName =
    stringifiedValue != null &&
    EDamageSeverity[stringifiedValue as keyof typeof EDamageSeverity]
      ? EDamageSeverity[stringifiedValue as keyof typeof EDamageSeverity]
      : content.damageSeverity.none;

  return (
    <StyledButton
      $severityLevel={value}
      disabled={isDisabled}
      onClick={() =>
        currentValueIndex === 0
          ? handleChangeSeverity(EChangeSeverityType.Increase)
          : () => {}
      }
      variant='outlined'
    >
      {value ? (
        <IconButton
          className='damageTicker-decreaseButton'
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={() => handleChangeSeverity(EChangeSeverityType.Decrease)}
          sx={{ height: 28, width: 28 }}
        >
          <Remove fontSize='small' />
        </IconButton>
      ) : (
        <div style={{ width: 28 }} />
      )}

      <LayoutJay gap='none' sx={{ margin: "0 auto" }}>
        <Typography className='damageTicker-label' variant='caption'>
          {label}
        </Typography>
        <Typography className='damageTicker-value' variant='overline'>
          {severityName}
        </Typography>
      </LayoutJay>

      {value && !isIncreaseDisabled ? (
        <IconButton
          className='damageTicker-increaseButton'
          disabled={isIncreaseDisabled}
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={() => handleChangeSeverity(EChangeSeverityType.Increase)}
          sx={{ height: 28, width: 28 }}
        >
          <Add fontSize='small' />
        </IconButton>
      ) : (
        <div style={{ width: 28 }} />
      )}
    </StyledButton>
  );
};

export default DamageSeverityTicker;
